import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

function RenewModal({ show, handleClose, license, onRenewSuccess }) {
  const [formData, setFormData] = useState({
    registrationNumber: "",
    renewalPeriod: "",
    renewalDate: "",
    expDate: "",
    amount: "",
    billNo: "",
  });
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchLastRegistration = async () => {
      if (license && show) {
        setLoading(true);
        try {
          const response = await axios.get(
            `${API_URL}get-reg-license.php?id=${license.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.data.success && response.data.data) {
            const lastReg = response.data.data;
            // The API returns a datetime string, so we split it to get only the date part
            const expiryDateOnly = lastReg.expire_date
              ? lastReg.expire_date.split(" ")[0]
              : "";
            setFormData({
              registrationNumber: lastReg.reg_no || license.reg_no,
              billNo: lastReg.bill_no || "",
              renewalDate: expiryDateOnly,
              renewalPeriod: "",
              amount: "",
              expDate: "",
            });
          } else {
            setFormData((prev) => ({
              ...prev,
              registrationNumber: license.reg_no,
              renewalDate: new Date().toISOString().slice(0, 10),
            }));
          }
        } catch (error) {
          toast.error("Failed to fetch previous registration details.");
          setFormData((prev) => ({
            ...prev,
            registrationNumber: license.reg_no,
            renewalDate: new Date().toISOString().slice(0, 10),
          }));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchLastRegistration();
  }, [license, show, API_URL, token]);

  useEffect(() => {
    if (formData.renewalDate && formData.renewalPeriod) {
      const startDate = new Date(formData.renewalDate);
      const validityYears = parseInt(formData.renewalPeriod, 10);

      if (!isNaN(validityYears)) {
        startDate.setFullYear(startDate.getFullYear() + validityYears);
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, "0");
        const day = String(startDate.getDate()).padStart(2, "0");
        const newExpDate = `${year}-${month}-${day}`;
        setFormData((prevData) => ({ ...prevData, expDate: newExpDate }));
      }
    }
  }, [formData.renewalDate, formData.renewalPeriod]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!license || !license.id) {
      toast.error("License ID is missing.");
      return;
    }

    const apiPayload = {
      id: license.id,
      trade_name: license.trade_name,
      trade_owner: license.trade_owner,
      validity_peroid: `${formData.renewalPeriod} Year`,
      amount: formData.amount,
      reg_date: formData.renewalDate,
      expire_date: formData.expDate,
      reg_no: formData.registrationNumber || "",
      bill_no: formData.billNo || "",
    };

    try {
      const response = await axios.post(
        `${API_URL}renew-trade-license.php`,
        apiPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "License Renewed Successfully!");
        if (onRenewSuccess) {
          onRenewSuccess();
        }
        handleClose();
      } else {
        toast.error(response.data.message || "Failed to renew license.");
      }
    } catch (error) {
      toast.error("An error occurred while renewing the license.");
    }
  };

  if (!license) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Renew Trade License</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Trade Name</Form.Label>
              <Form.Control
                type="text"
                value={license.trade_name}
                readOnly
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Owner Name</Form.Label>
              <Form.Control
                type="text"
                value={license.trade_owner}
                readOnly
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Registration Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bill Number (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="billNo"
                value={formData.billNo}
                onChange={handleChange}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Renewal Period</strong>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="renewalPeriod"
                    value={formData.renewalPeriod}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Period...</option>
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    <option value="4">4 Years</option>
                    <option value="5">5 Years</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={formData.amount}
                    placeholder="₹"
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Renewal Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="renewalDate"
                    value={formData.renewalDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>New Expiration Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="expDate"
                    value={formData.expDate}
                    readOnly
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="secondary"
                onClick={handleClose}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="info" type="submit">
                Renew License
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default RenewModal;
